import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Loader2, Store, X } from 'lucide-react';
import ListingCard from '@/components/marketplace/ListingCard';
import { browseListings } from '@/lib/marketplace';
import { MARKETPLACE } from '@/config/earnings';

// ─── Browse the marketplace ───────────────────────────────────────────────────
// Search, a category filter and a sort, over a grid that loads more on demand.
// Used by the marketplace page and by the dashboard's Marketplace section, so
// the two always behave the same.

const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'popular', label: 'Best selling' },
  { id: 'price_asc', label: 'Price: low to high' },
  { id: 'price_desc', label: 'Price: high to low' },
];

const PAGE_SIZE = 12;

export default function MarketplaceBrowser({ emptyAction = null }) {
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ listings: [], total: 0, pricing: { symbol: '$', locale: 'en-US' }, loading: true, error: '' });
  const requestRef = useRef(0);

  // Typing settles for a moment before it searches.
  useEffect(() => {
    const t = setTimeout(() => { setQ(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async (nextPage) => {
    const id = ++requestRef.current;
    setState(s => ({ ...s, loading: true, error: '' }));
    try {
      const data = await browseListings({ q, category, sort, page: nextPage, limit: PAGE_SIZE });
      if (id !== requestRef.current) return;
      setState(s => ({
        listings: nextPage === 1 ? data.listings : [...s.listings, ...data.listings],
        total: data.total,
        pricing: { symbol: data.symbol, locale: data.locale, currency: data.currency },
        loading: false,
        error: '',
      }));
    } catch (err) {
      if (id !== requestRef.current) return;
      setState(s => ({ ...s, loading: false, error: err.message || 'Could not load the marketplace.' }));
    }
  }, [q, category, sort]);

  useEffect(() => { load(page); }, [load, page]);

  const filtering = !!(q || category);
  const hasMore = state.listings.length < state.total;

  return (
    <div>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <label className="relative flex-1 min-w-0">
          <span className="sr-only">Search sites</span>
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sites — bakery, portfolio, real estate…"
            className="w-full h-11 pl-10 pr-10 rounded-xl border border-zinc-800 bg-zinc-900 text-sm text-white placeholder-zinc-600 outline-none focus:border-orange-500"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </label>
        <label className="shrink-0">
          <span className="sr-only">Sort</span>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="h-11 w-full lg:w-auto rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-200 outline-none focus:border-orange-500"
          >
            {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="group" aria-label="Categories">
        <button
          onClick={() => { setCategory(''); setPage(1); }}
          aria-pressed={!category}
          className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
            !category ? 'bg-white text-zinc-950 border-white' : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
          }`}
        >
          All
        </button>
        {MARKETPLACE.categories.map(c => (
          <button
            key={c.id}
            onClick={() => { setCategory(c.id); setPage(1); }}
            aria-pressed={category === c.id}
            className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
              category === c.id ? 'bg-white text-zinc-950 border-white' : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {state.error && (
        <p role="alert" className="mt-6 text-sm text-red-400">{state.error}</p>
      )}

      {!state.loading && !state.error && state.listings.length === 0 ? (
        <div className="mt-8 border border-dashed border-zinc-800 rounded-2xl p-10 text-center">
          <Store className="w-9 h-9 text-zinc-700 mx-auto mb-3" />
          <p className="text-white font-semibold">{filtering ? 'Nothing matches that yet' : 'The marketplace is just opening'}</p>
          <p className="text-zinc-500 text-sm mt-1">
            {filtering ? 'Try another search or category.' : 'Be one of the first to sell a site here.'}
          </p>
          {emptyAction && <div className="mt-5">{emptyAction}</div>}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {state.listings.map(l => <ListingCard key={l.id} listing={l} pricing={state.pricing} />)}
          {state.loading && state.listings.length === 0 && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden animate-pulse">
              <div className="h-44 bg-zinc-800" />
              <div className="p-4 space-y-2"><div className="h-3 w-1/3 bg-zinc-800 rounded" /><div className="h-4 w-3/4 bg-zinc-800 rounded" /></div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={state.loading}
            className="h-11 px-6 rounded-full border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-sm font-semibold text-zinc-200 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {state.loading && <Loader2 className="w-4 h-4 animate-spin" />} Load more
          </button>
        </div>
      )}
    </div>
  );
}
